import { createElement } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import {
    List,
    ListItem,
    ListItemText,
    Alert,
    Box,
    Chip,
    Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useTranslate } from 'react-admin';
import { IfCanAccess } from '@react-admin/ra-rbac';
import {
    numberFormatter,
    appIcons,
    resolveDate,
    daysBetweenDates,
} from './common';
import { Filter, Handler, LoginStats as LoginStatsData } from './types';
import StaleDataAlert from './StaleDataAlert';

const codeLabel = (translate: ReturnType<typeof useTranslate>, code: string) =>
    translate(`statistics.loginstats.codes.${code}`, {
        _: translate('statistics.loginstats.codes.UNKNOWN', { _: code }),
    });

const percentFormatter = new Intl.NumberFormat('fi-FI', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
});

const formatPercent = (part: number, total: number) => {
    if (!total) return '0 %';
    const value = (part / total) * 100;
    if (value > 0 && value < 0.1) return '< 0,1 %';
    return `${percentFormatter.format(value)} %`;
};

export const LoginFailureList = ({ data }: { data: LoginStatsData }) => {
    const translate = useTranslate();
    const sortedFailures = [...(data.failed_by_code || [])].sort(
        (a, b) => b.count - a.count
    );

    return (
        <List disablePadding sx={{ pb: 1 }}>
            {sortedFailures.length === 0 ? (
                <ListItem>
                    <ListItemText
                        secondary={translate('statistics.loginstats.nofailed')}
                    />
                </ListItem>
            ) : (
                sortedFailures.map(item => (
                    <ListItem
                        key={item.code}
                        sx={{
                            pl: 3,
                            py: 0.5,
                            gap: 1.5,
                            alignItems: 'baseline',
                        }}
                    >
                        <ListItemText
                            primary={codeLabel(translate, item.code)}
                            slotProps={{
                                primary: { variant: 'body2' },
                            }}
                            sx={{ my: 0 }}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                whiteSpace: 'nowrap',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {numberFormatter.format(item.count)} (
                            {formatPercent(item.count, data.failed)})
                        </Typography>
                    </ListItem>
                ))
            )}
        </List>
    );
};

const LoginStatsByCode = (data: LoginStatsData) => {
    const translate = useTranslate();

    if (!data || data.total === 0) {
        return (
            <Alert severity="warning" sx={{ padding: 10 }}>
                {translate('statistics.nodata')}
            </Alert>
        );
    }

    return (
        <>
            <List>
                <ListItem>
                    <ListItemText
                        primary={`${translate(
                            'statistics.loginstats.success'
                        )} ${numberFormatter.format(data.success)}`}
                        secondary={`${formatPercent(data.success, data.total)} \u2013 MPASS ${numberFormatter.format(
                            data.mpass?.success || 0
                        )} (${formatPercent(data.mpass?.success || 0, data.success)})`}
                    />
                    <Chip
                        color="success"
                        label={numberFormatter.format(data.success)}
                    />
                </ListItem>
                <ListItem>
                    <ListItemText
                        primary={`${translate(
                            'statistics.loginstats.failed'
                        )} ${numberFormatter.format(data.failed)}`}
                        secondary={`${formatPercent(data.failed, data.total)}${
                            data.mpass?.failed
                                ? ` \u2013 MPASS ${numberFormatter.format(data.mpass.failed)} (${formatPercent(
                                      data.mpass.failed,
                                      data.failed
                                  )})`
                                : ''
                        }`}
                    />
                    <Chip
                        color="error"
                        label={numberFormatter.format(data.failed)}
                    />
                </ListItem>
            </List>
            <LoginFailureList data={data} />
        </>
    );
};

const LoginStatsGraph = (data: LoginStatsData) => {
    const translate = useTranslate();

    if (!data || data.total === 0) {
        return (
            <Alert severity="warning" sx={{ padding: 10 }}>
                {translate('statistics.nodata')}
            </Alert>
        );
    }

    const sortedFailures = [...(data.failed_by_code || [])].sort(
        (a, b) => b.count - a.count
    );

    const xAxis = [
        translate('statistics.loginstats.success'),
        ...sortedFailures.map(item => codeLabel(translate, item.code)),
    ];
    const series = [
        {
            data: [data.success, ...sortedFailures.map(() => 0)],
            label: translate('statistics.loginstats.success'),
        },
        {
            data: [0, ...sortedFailures.map(item => item.count)],
            label: translate('statistics.loginstats.failed'),
        },
    ];

    return (
        <BarChart
            height={400}
            series={series}
            xAxis={[{ data: xAxis, scaleType: 'band' }]}
        />
    );
};

const LoginStats = (
    props: { data: LoginStatsData | null } & Handler & Filter
) => {
    const translate = useTranslate();
    const { data, handler, from, to, mode } = props;
    const total = numberFormatter.format(data?.total || 0);
    const dateFrom = resolveDate(from);
    const dateTo = resolveDate(to);
    const days = daysBetweenDates(dateFrom, dateTo);
    const title = translate('statistics.loginstats.title');
    const tooltip = translate('statistics.loginstats.tooltip', { day: days });

    return (
        <>
            {createElement(handler, {
                title: days === 1 ? title : tooltip,
                subtitle: String(total),
                tooltip: days === 1 ? tooltip : null,
                icon: appIcons.login,
                children: (
                    <Box>
                        <StaleDataAlert
                            stale={data?.stale}
                            cachedAt={data?.cached_at}
                        />
                        {(() => {
                            switch (mode) {
                                case 'List':
                                    return LoginStatsByCode(data);
                                case 'Graph':
                                    return LoginStatsGraph(data);
                                default:
                                    return null;
                            }
                        })()}
                        <IfCanAccess action="show" resource="loginstats">
                            <Box sx={{ p: 1, textAlign: 'right' }}>
                                <Link to="/loginstats">
                                    <Typography
                                        variant="body2"
                                        color="primary"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        <TimelineIcon fontSize="small" />
                                        {translate(
                                            'statistics.activeusershistory.show_page'
                                        )}
                                    </Typography>
                                </Link>
                            </Box>
                        </IfCanAccess>
                    </Box>
                ),
            })}
        </>
    );
};

export default LoginStats;
